import { useRecoilState, useRecoilValue } from "recoil";
import AddButton from "../../../../components/AddButton";
import {
    SecurityAddState,
    SecurityEditState,
    RequiredInput,
    FireAlarmSystemForEdit,
    tempFireAlarmSystems,
    TempFireAlarmSystem,
    FireAlarmSystemModalState,
    FireAlarmSystemAddState,
    FireAlarmSystemSaving,
    FireAlarmSystemDeactivateModal,
} from "../atoms/atoms";
import { IFireAlarmSystem, ISecurityDetails } from "../types";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useVendors } from "../hooks/useVendors";
import { useDataConnection } from "../hooks/useDataConnection";
import { useAlarmType } from "../hooks/useAlarmType";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "../../../../components/toastNotification";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import moment from "moment";
import FireAlarmSystemCard from "components/FireAlarmSystemCard";
import SecurityFireAlarmSystemForm from "components/SecurityFireAlarmSystemForm";
import { useFireAlarmSystemsByRecordNumber } from "../hooks/useFireAlarmSystemsByRecordNumber";
import { useGetFireAlarmSensorOptions } from "../hooks/useGetFireAlarmSensorOptions";
import { useGetFloorCountOptions } from "../hooks/useGetFloorCountOptions";
import useAddSingleFireAlarmSystem from "../hooks/useAddSingleFireAlarmSystem";
import useEditSingleFireAlarmSystem from "../hooks/useEditSingleFireAlarmSystem";
import useDeleteFireAlarmSystem from "../hooks/useDeleteFireAlarmSystem";

interface FirstAlarmSystemProps {
    fireAlarmSystems?: Array<IFireAlarmSystem>;
    rowForEdit: ISecurityDetails | undefined;
}
const defaultFireAlarmSystem = [
    {
        parentRow: true,
        tableName: "fireAlarmSystem",
        vendorId: null,
    },
] as Array<IFireAlarmSystem>;

const FireAlarmSystemForm = ({
    rowForEdit,
    fireAlarmSystems = defaultFireAlarmSystem,
}: FirstAlarmSystemProps): JSX.Element => {
    const [isShowFireAlarmSystemModal, setShowFireAlarmSystemModal] = useRecoilState(FireAlarmSystemAddState);
    const [isAddState] = useRecoilState(SecurityAddState);
    const [isEditState] = useRecoilState(SecurityEditState);
    const [, setIsAddInEditState] = useState(false);
    const [isEditInAddState, setIsEditInAddState] = useState(false);
    const [showFireAlarmSystemCardModal, setShowFireAlarmSystemCardModal] =
        useRecoilState(FireAlarmSystemModalState);
    const [isSaving, setIsSaving] = useRecoilState(FireAlarmSystemSaving);
    const [, setIsRequired] = useRecoilState(RequiredInput);
    const [showDeactivateModal, setShowDeactivateModal] =
        useRecoilState(FireAlarmSystemDeactivateModal);
    const queryClient = useQueryClient();
    const [forDeleteDetails, setForDeleteDetails] = useState();
    const [hasDeletePermission, setHasDeletePermission] = useState(false);
    const [, setHasFirstFieldValue] = useState(true);
    const [clickCount, setClickCount] = useState(0);
    const [fireAlarmForEditData, setFireAlarmForEditData] =
        useRecoilState<any>(FireAlarmSystemForEdit);

    //Permission
    const userPermissions = useRecoilValue(permissionsSelector);
    const addPermission = PERMISSIONS.permission_security_add;
    const deletePermission = PERMISSIONS.permission_security_delete;

    const { control, watch, setValue, getValues } = useFormContext();
    const [isPhoneConnectionRequired, setPhoneConnectionRequiredState] = useState<boolean>(false);

    const formMethods = useFormContext();

    const { data: Vendors } = useVendors();
    const vendorsOption = Vendors?.data?.map(
        (x: {
            id: number;
            name: string;
            contact: string;
            email: string;
            city: string;
            accountNumber: string;
            state: string;
            street: string;
            vendorType: number;
            zipcode: string;
            phone: string;
            tonerRequestEmail: string;
            tonerRequestPhone: string;
        }) => {
            return {
                id: x.id,
                value: x.id,
                label: x.name,
                email: x.email,
                accountNumber: x.accountNumber,
                address:
                    (x.street ? x.street : "") +
                    " " +
                    (x.city ? x.city : "") +
                    " " +
                    (x.state ? x.state : "") +
                    " " +
                    (x.zipcode ? x.zipcode : ""),
                vendorType: x.vendorType,
                vendorPhone: x.phone,
                trEmail: x.tonerRequestEmail,
                tonerPhone: x.tonerRequestPhone,
                contact: x.contact,
            };
        }
    );

    // Data Connection Field Options
    const { data: dataConnection } = useDataConnection();
    const dataConnOption = dataConnection?.data?.map(
        (x: { id: number; dataConnectionName: string }) => {
            return {
                id: x.id,
                value: x.id,
                label: x.dataConnectionName,
            };
        }
    );

    // Alarm Type Field Options
    const { data: alarmType } = useAlarmType();
    const alarmTypeOption = alarmType?.data?.map(
        (x: { id: number; alarmTypeName: string }) => {
            return {
                id: x.id,
                value: x.id,
                label: x.alarmTypeName,
            };
        }
    );

    // Fire Alarm Sensor Options
    const { data: fireAlarmSensors } = useGetFireAlarmSensorOptions();
    const fireAlarmSensorOptions = fireAlarmSensors?.data?.map(
        (x: { fireAlarmSystemSensorId: number; fireAlarmSystemSensor: string }) => {
            return {
                id: x.fireAlarmSystemSensorId,
                value: x.fireAlarmSystemSensorId,
                label: x.fireAlarmSystemSensor,
            };
        }
    );

    // Number of Floor Options
    const { data: numberOfFloors } = useGetFloorCountOptions();
    const numberOfFloorOptions = numberOfFloors?.data?.map(
        (x: { floorCountId: number; floorCount: string }) => {
            return {
                id: x.floorCountId,
                value: x.floorCountId,
                label: x.floorCount,
            };
        }
    );

    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: "fireAlarmSystem",
    });

    const MAX_CARD = 10;
    const tableArray = fireAlarmSystems.length > 0 && watch(`${fireAlarmSystems[0].tableName}`);
    const controlledFields =
        tableArray &&
        fields.map((field, index) => ({
            ...field,
            ...tableArray[index],
        }));

    // SETTING DEFAULT TABLE VALUES
    useEffect(() => {
        if (fireAlarmSystems) {
            replace(fireAlarmSystems);
        }
    }, [fireAlarmSystems]);

    //Add Validation
    const [initialAddFireAlarmSystems, setInitialAddFireAlarmSystems] = useRecoilState(tempFireAlarmSystems);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    //dirty checker

    const isEmpty = (value: any): boolean => {
        return value === null || value === 0 || value === "" || value === undefined;
    };

    const requiredFields = ["vendorId"];

    const optionalFields = [
        "vendor",
        "vendorPhone",
        "vendorAccountNumber",
        "siteNumber",
        "dataConnection",
        "dataConnectionId",
        "connectionDetails",
        "alarmType",
        "fireAlarmTypeId",
        "numberOfFireAlarmSensorsId",
        "numberOfFloorCountId",
        "specialEquipment",
        "installDate",
        "cancellationDate",
        "ipaddress",
        "portNumber",
        "ipGateway",
        "subnetMask",
        "dns",
        "phoneConnection",
    ];

    const isFireAlarmDirty = (fireAlarmSystems: any[]): boolean => {
        return fireAlarmSystems.some((fireAlarmSystem) => {
            const missingRequired = requiredFields.some((field) =>
                isEmpty(fireAlarmSystem[field])
            );
            if (missingRequired) return false;

            const hasOptionalData = optionalFields.some(
                (field) => !isEmpty(fireAlarmSystem[field])
            );
            return hasOptionalData;
        });
    };

    const checkEmptyFields = (buttonState) => {
        if (buttonState === "add") {
            setClickCount(clickCount + 1);
            watch("fireAlarmSystem[0].vendorId")
                ? setHasFirstFieldValue(true)
                : setHasFirstFieldValue(false);
        }
        if (buttonState === "proceed") {
            const fieldLen = controlledFields.length;
            let hasError = false;
            let filledVendorCount = 0;

            controlledFields.forEach((obj: IFireAlarmSystem) => {
                Object.entries(obj).forEach(([key, value]) => {

                    if (key === "vendorId") {
                        if (value) {
                            filledVendorCount += 1;
                        } else {
                            hasError = true;
                            setClickCount(prev => prev + 1);
                            setIsRequired(true);
                            setHasFirstFieldValue(false);

                            toast.fire(
                                "Information",
                                "Please fill the Vendor Name to proceed.",
                                "info"
                            );
                        }
                    }

                    if (key === "phoneConnection") {
                        if (value?.trim()) {
                            const isComplete = /^\d{3}-\d{3}-\d{4}$/.test(value);

                            if (!isComplete) {
                                hasError = true;
                                setClickCount(prev => prev + 1);
                                setPhoneConnectionRequiredState(true);

                                toast.fire(
                                    "Information",
                                    "Phone Connection is optional, but must be fully formatted (e.g., 111-111-1111) if provided.",
                                    "info"
                                );
                            } else {
                                setPhoneConnectionRequiredState(false);
                            }
                        }
                    }

                });
            });

            if (!hasError && filledVendorCount === fieldLen) {
                setShowFireAlarmSystemModal(false);
                setPhoneConnectionRequiredState(false);
            }

        }
    };

    //Set Edit default values
    const setEditFireAlarmSystemValues = (fireAlarmSystem: IFireAlarmSystem) => {
        if (!fireAlarmSystem) return;
        const valuesToSet = {
            fireAlarmSystemId: fireAlarmSystem.fireAlarmSystemId,
            vendorId: fireAlarmSystem.vendorId,
            vendorPhone: fireAlarmSystem.vendorPhone,
            vendorAccountNumber: fireAlarmSystem.vendorAccountNumber,
            siteNumber: fireAlarmSystem.siteNumber,
            dataConnectionId: fireAlarmSystem.dataConnectionId,
            installDate: fireAlarmSystem.installDate?.slice(0, 10),
            connectionDetails: fireAlarmSystem.connectionDetails,
            fireAlarmTypeId: fireAlarmSystem.fireAlarmTypeId,
            numberOfFireAlarmSensorsId: fireAlarmSystem.numberOfFireAlarmSensorsId,
            numberOfFloorsId: fireAlarmSystem.numberOfFloorsId,
            specialEquipment: fireAlarmSystem.specialEquipment,
            cancellationDate: fireAlarmSystem.cancellationDate?.slice(0, 10),
            isActive: fireAlarmSystem.isActive,
            createdBy: fireAlarmSystem.createdBy,
            createdDate: fireAlarmSystem.createdDate,
            ipaddress: fireAlarmSystem.ipaddress,
            ipGateway: fireAlarmSystem.ipGateway,
            subnetMask: fireAlarmSystem.subnetMask,
            dns: fireAlarmSystem.dns,
            portNumber: fireAlarmSystem.portNumber,
            phoneConnection: fireAlarmSystem.phoneConnection,
        };

        Object.entries(valuesToSet).forEach(([key, value]) => {
            setValue(key, value);
        });

        // use to delay for isActive checkbox
        queryClient.invalidateQueries("GetAlarmsByRecordNumber");
        setShowFireAlarmSystemCardModal(true);
    };
    //Single Add Initialization
    const {
        mutate: addSingleFireAlarmSystem,
        isSuccess: addFireAlarmSystemSuccess,
        data: addFireAlarmSystemResult,
        reset: resetAddFireAlarmSystemData,
    } = useAddSingleFireAlarmSystem();

    //Single Edit Initialization
    const {
        mutate: editSingleFireAlarmSystem,
        isSuccess: editFireAlarmSystemSuccess,
        data: editFireAlarmSystemResult,
        reset: resetEditFireAlarmSystemData,
    } = useEditSingleFireAlarmSystem();

    //Delete
    const {
        mutate: deactivateFireAlarmSystem,
        isSuccess: deactivateFireAlarmSystemSuccess,
        data: deactivateFireAlarmSystemResult,
        reset: resetDeactivateFireAlarmSystemData,
    } = useDeleteFireAlarmSystem();

    //Reset form data sing Add
    const resetData = () => {
        const fireAlarmDefaults = {
            "fireAlarmSystem[0].vendorId": "",
            "fireAlarmSystem[0].vendorPhone": "",
            "fireAlarmSystem[0].accountNumber": "",
            "fireAlarmSystem[0].alarmSiteNumber": "",
            "fireAlarmSystem[0].dataConnectionId": "",
            "fireAlarmSystem[0].installDate": "",
            "fireAlarmSystem[0].connectionDetails": "",
            "fireAlarmSystem[0].alarmTypeId": "",
            "fireAlarmSystem[0].numberOfFireAlertSensorsId": "",
            "fireAlarmSystem[0].numberOfFloorCountId": "",
            "fireAlarmSystem[0].specialEquipment": "",
            "fireAlarmSystem[0].cancellationDate": "",
            "fireAlarmSystem[0].ipaddress": "",
            "fireAlarmSystem[0].ipGateway": "",
            "fireAlarmSystem[0].subnetMask": "",
            "fireAlarmSystem[0].dns": "",
            "fireAlarmSystem[0].phoneConnection": "",
            "fireAlarmSystem[0].portNumber": "",
        };

        Object.entries(fireAlarmDefaults).forEach(([key, value]) => {
            setValue(key, value);
        });
    };

    //Save Get values for single add
    const handleAddSave = () => {
        const submittedValues = getValues();
        const fireAlarmSystem = submittedValues.fireAlarmSystem;

        const hasAlarm = fireAlarmSystem && fireAlarmSystem.length > 0;
        if (!hasAlarm) return;

        const isDirty = isFireAlarmDirty(fireAlarmSystem);
        if (!isDirty) {
            setIsRequired(true);
            toast.fire(
                "Information",
                "Please fill the vendor field to save.",
                "info"
            );
            return;
        }
        else if (fireAlarmSystem[0].phoneConnection && !(/^\d{3}-\d{3}-\d{4}$/.test(fireAlarmSystem[0].phoneConnection))) {
            toast.fire(
                "Information",
                "Phone Connection is optional, but must be fully formatted (e.g., 111-111-1111) if provided.",
                "info"
            );
            setPhoneConnectionRequiredState(true);
            return;
        }

        setPhoneConnectionRequiredState(false);
        setIsSaving(true);

        const alarmData: IFireAlarmSystem = {
            recordNumber: rowForEdit?.recordNumber ?? null,
            vendorId: getValues("fireAlarmSystem[0].vendorId"),
            vendorPhone: getValues("fireAlarmSystem[0].vendorPhone"),
            vendorAccountNumber: getValues("fireAlarmSystem[0].vendorAccountNumber"),
            siteNumber: getValues("fireAlarmSystem[0].siteNumber")?.trim() || null,
            dataConnectionId: getValues("fireAlarmSystem[0].dataConnectionId"),
            installDate: getValues("fireAlarmSystem[0].installDate"),
            connectionDetails: getValues("fireAlarmSystem[0].connectionDetails")?.trim() || null,
            fireAlarmTypeId: getValues("fireAlarmSystem[0].fireAlarmTypeId"),
            numberOfFireAlarmSensorsId: getValues(
                "fireAlarmSystem[0].numberOfFireAlarmSensorsId"
            ),
            numberOfFloorsId: getValues(
                "fireAlarmSystem[0].numberOfFloorsId"
            ),
            specialEquipment: getValues("fireAlarmSystem[0].specialEquipment")?.trim() || null,
            cancellationDate: getValues("fireAlarmSystem[0].cancellationDate"),
            ipaddress: getValues("fireAlarmSystem[0].ipaddress")?.trim() || null,
            ipGateway: getValues("fireAlarmSystem[0].ipGateway")?.trim() || null,
            subnetMask: getValues("fireAlarmSystem[0].subnetMask")?.trim() || null,
            dns: getValues("fireAlarmSystem[0].dns")?.trim() || null,
            portNumber: getValues("fireAlarmSystem[0].portNumber")?.trim() || null,
            phoneConnection: getValues("fireAlarmSystem[0].phoneConnection")?.trim() || null,
        };
        addSingleFireAlarmSystem(alarmData);
        setShowFireAlarmSystemModal(false);
    };

    //Save Edit single fireAlarmSystem
    const handleEditSave = () => {
        setIsSaving(true);
        const fireAlarmNewData = {
            recordNumber: rowForEdit?.recordNumber!,
            fireAlarmSystemId: getValues("fireAlarmSystemId"),
            vendorId: getValues("vendorId"),
            vendorPhone: getValues("vendorPhone"),
            vendorAccountNumber: getValues("vendorAccountNumber"),
            siteNumber: getValues("siteNumber")?.trim() || null,
            dataConnectionId: getValues("dataConnectionId"),
            installDate: getValues("installDate")
                ? getValues("installDate").slice(0, 10)
                : null,
            connectionDetails: getValues("connectionDetails")?.trim() || null,
            fireAlarmTypeId: getValues("fireAlarmTypeId"),
            numberOfFireAlarmSensorsId: getValues("numberOfFireAlarmSensorsId"),
            numberOfFloorsId: getValues("numberOfFloorsId"),
            specialEquipment: getValues("specialEquipment")?.trim() || null,
            cancellationDate: getValues("cancellationDate")
                ? getValues("cancellationDate").slice(0, 10)
                : null,
            isActive: getValues("isActive"),
            createdBy: getValues("createdBy"),
            createdDate: getValues("createdDate"),
            ipaddress: getValues("ipaddress")?.trim() || null,
            portNumber: getValues("portNumber")?.trim() || null,
            ipGateway: getValues("ipGateway")?.trim() || null,
            subnetMask: getValues("subnetMask")?.trim() || null,
            dns: getValues("dns")?.trim() || null,
            phoneConnection: getValues("phoneConnection")?.trim() || null
        };
        if (!fireAlarmNewData.vendorId) {
            toast.fire("Information", "Please fill the Vendor Name to save.", "info");
            setIsRequired(true);
            setIsSaving(false);
            return;
        }
        else if (fireAlarmNewData.phoneConnection && !(/^\d{3}-\d{3}-\d{4}$/.test(fireAlarmNewData.phoneConnection))) {
            toast.fire(
                "Information",
                "Phone Connection is optional, but must be fully formatted (e.g., 111-111-1111) if provided.",
                "info"
            );
            setPhoneConnectionRequiredState(true);
            setIsSaving(false);
            return;
        }

        setPhoneConnectionRequiredState(false);
        const fireAlarmOldData = {
            recordNumber: fireAlarmForEditData?.recordNumber,
            fireAlarmSystemId: fireAlarmForEditData?.fireAlarmSystemId,
            vendorId: fireAlarmForEditData?.vendorId,
            vendorPhone: fireAlarmForEditData?.vendorPhone,
            vendorAccountNumber: fireAlarmForEditData?.vendorAccountNumber,
            siteNumber: fireAlarmForEditData?.siteNumber,
            dataConnectionId: fireAlarmForEditData?.dataConnectionId,
            installDate: fireAlarmForEditData?.installDate
                ? fireAlarmForEditData?.installDate?.slice(0, 10)
                : null,
            connectionDetails: fireAlarmForEditData?.connectionDetails,
            fireAlarmTypeId: fireAlarmForEditData?.fireAlarmTypeId,
            numberOfFireAlarmSensorsId: fireAlarmForEditData?.numberOfFireAlarmSensorsId,
            numberOfFloorsId: fireAlarmForEditData?.numberOfFloorsId,
            specialEquipment: fireAlarmForEditData?.specialEquipment,
            cancellationDate: fireAlarmForEditData?.cancellationDate
                ? fireAlarmForEditData?.cancellationDate?.slice(0, 10)
                : null,
            isActive: fireAlarmForEditData?.isActive,
            createdBy: fireAlarmForEditData?.createdBy,
            createdDate: fireAlarmForEditData?.createdDate,
            ipaddress: fireAlarmForEditData?.ipaddress,
            portNumber: fireAlarmForEditData?.portNumber,
            ipGateway: fireAlarmForEditData?.ipGateway,
            subnetMask: fireAlarmForEditData?.subnetMask,
            dns: fireAlarmForEditData?.dns,
            phoneConnection: fireAlarmForEditData?.phoneConnection
        };

        const newData = JSON.stringify(fireAlarmNewData);
        const oldData = JSON.stringify(fireAlarmOldData);

        if (newData === oldData) {
            toast.fire("Saving Failed", "No changes have been made.", "warning");
            setIsSaving(false);
        } else {
            setIsSaving(true);
            editSingleFireAlarmSystem(fireAlarmNewData);
            setShowFireAlarmSystemCardModal(false);
        }
    };

    //Deactivate
    const handleDeactivate = () => {
        deactivateFireAlarmSystem(forDeleteDetails);
        setShowDeactivateModal(false);
    };

    //Modal Messages
    const deleteMessage = (
        <div className="text-gray-500 font-normal">
            {"Are you sure you want to deactivate this Fire Alarm System?"}
        </div>
    );

    const removeMessage = (
        <div className="text-gray-500 font-normal">
            {"Are you sure you want to remove this Fire Alarm System?"}
        </div>
    );

    //Toasters
    if (addFireAlarmSystemSuccess && addFireAlarmSystemResult) {
        if (addFireAlarmSystemResult.status === 201 || addFireAlarmSystemResult.status === 200) {
            toast.fire("Success", "Fire Alarm System added successfully.", "success");
            queryClient.invalidateQueries("GetFireAlarmSystemsByRecordNumber");
        }
        setIsSaving(false);
        resetAddFireAlarmSystemData();
        resetData();
    }
    if (editFireAlarmSystemSuccess && editFireAlarmSystemResult) {
        if (editFireAlarmSystemResult.status === 201 || editFireAlarmSystemResult.status === 200) {
            toast.fire("Success", "Fire Alarm System updated successfully.", "success");
            queryClient.invalidateQueries("GetFireAlarmSystemsByRecordNumber");
        }
        setIsSaving(false);
        resetEditFireAlarmSystemData();
    }
    if (deactivateFireAlarmSystemSuccess && deactivateFireAlarmSystemResult) {
        if (deactivateFireAlarmSystemResult.status === 201 || deactivateFireAlarmSystemResult.status === 200) {
            toast.fire("Success", "Fire Alarm System deactivated successfully.", "success");
            queryClient.invalidateQueries("GetFireAlarmSystemsByRecordNumber");
        }
        resetDeactivateFireAlarmSystemData();
    }

    const { data: fireAlarmSystemsData, isLoading: fetchingFireAlarmSystems } = useFireAlarmSystemsByRecordNumber(
        rowForEdit ? rowForEdit?.recordNumber : 0
    );

    useEffect(() => {
        if (userPermissions.includes(deletePermission)) {
            setHasDeletePermission(true);
        }
    }, []);

    //reset form when adding
    const resetAlarmForm = () => {
        remove();
        append({
            vendorId: null,
            vendor: "",
            vendorPhone: "",
            contractRenewalDate: null,
            accountNumber: "",
            alarmSiteNumber: "",
            dataConnection: "",
            dataConnectionId: null,
            connectionDetails: "",
            alarmTypeId: 0,
            alarmType: "",
            numberOfMotionSensors: "",
            numberOfDoorContacts: "",
            numberOfPanicButtons: "",
            specialEquipment: "",
            alarmInstallDate: "",
            cancellationDate: "",
            ipaddress: "",
            portNumber: "",
            ipGateway: "",
            subnetMask: "",
            dns: "",
        });

        setFireAlarmForEditData(null);
        setEditingIndex(null);
    };

    //INITIAL ADD
    //removing of array in initial add
    const [indexToRemove, setIndexToRemove] = useState<number | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);

    const handleRemove = () => {
        if (indexToRemove !== null) {
            setInitialAddFireAlarmSystems((prevData) =>
                prevData.filter((_, i) => i !== indexToRemove)
            );
            setIndexToRemove(null);
        }
        setShowRemoveModal(false);
    };
    const handleOpenCardModalRemove = (index) => {
        setIndexToRemove(index);
        setShowRemoveModal(true);
    };

    const handleInitialAdd = (buttonState) => {
        if (buttonState === "proceed") {
            const submittedValues = getValues();
            const phoneConnection = submittedValues.fireAlarmSystem[0].phoneConnection;

            const isDirty = isFireAlarmDirty(submittedValues.fireAlarmSystem);
            const isCompletePhoneNumber = /^\d{3}-\d{3}-\d{4}$/.test(phoneConnection);

            if (!isDirty || (!!phoneConnection && !isCompletePhoneNumber)) return;

            const fireAlarmSystemDtos: TempFireAlarmSystem[] = submittedValues.fireAlarmSystem.map((item: any) => {
                const fireAlarmTypeName =
                    alarmTypeOption?.find((a) => a.id === item.fireAlarmTypeId)?.label || "";
                const vendorName =
                    vendorsOption?.find((v) => v.id === item.vendorId)?.label || "";
                const vendorPhone =
                    vendorsOption?.find((v) => v.id === item.vendorId)?.vendorPhone || "";
                const dataConnectionName =
                    dataConnOption?.find((d) => d.id === item.dataConnectionId)?.label ||
                    "";

                return {
                    vendorId: item.vendorId ?? null,
                    vendorName: vendorName,
                    vendorPhone: vendorPhone,
                    vendorAccountNumber: item.vendorAccountNumber || "",
                    siteNumber: item.siteNumber || "",
                    dataConnection: dataConnectionName,
                    dataConnectionId: item.dataConnectionId ?? null,
                    connectionDetails: item.connectionDetails || "",
                    fireAlarmTypeId: item.fireAlarmTypeId || null,
                    alarmTypeName: fireAlarmTypeName,
                    numberOfFireAlarmSensorsId: item.numberOfFireAlarmSensorsId || "",
                    numberOfFloorsId: item.numberOfFloorsId || "",
                    specialEquipment: item.specialEquipment || "",
                    installDate: item.installDate || "",
                    cancellationDate: item.cancellationDate || "",
                    isActive: item.isActive ?? true,
                    ipaddress: item.ipaddress || "",
                    portNumber: item.portNumber || "",
                    ipGateway: item.ipGateway || "",
                    subnetMask: item.subnetMask || "",
                    dns: item.dns || "",
                    phoneConnection: item.phoneConnection || "",
                    recordNumber: 0,
                };
            });

            setInitialAddFireAlarmSystems((prev) => [...(prev || []), ...fireAlarmSystemDtos]);
        }
    };

    const handleInitialEdit = () => {
        if (editingIndex === null) return;

        const formData = getValues();

        if (!formData.vendorId) {
            toast.fire(
                "Information",
                "Please fill the Vendor Name to proceed.",
                "info"
            );
            setIsRequired(true);

            return;
        }
        const fireAlarmTypeName =
            alarmTypeOption?.find((a) => a.id === formData.fireAlarmTypeId)?.label || "";
        const vendorName =
            vendorsOption?.find((v) => v.id === formData.vendorId)?.label || "";
        const vendorPhone =
            vendorsOption?.find((v) => v.id === formData.vendorId)?.vendorPhone || "";
        const dataConnection =
            dataConnOption?.find((d) => d.id === formData.dataConnectionId)?.label ||
            "";

        const payload: IFireAlarmSystem = {
            vendorId: formData.vendorId ?? null,
            vendorName: vendorName,
            vendorPhone,
            vendorAccountNumber: formData.vendorAccountNumber ?? "",
            siteNumber: formData.siteNumber ?? "",
            dataConnection,
            dataConnectionId: formData.dataConnectionId ?? null,
            connectionDetails: formData.connectionDetails ?? "",
            fireAlarmTypeId: formData.fireAlarmTypeId ?? 0,
            alarmTypeName: fireAlarmTypeName,
            numberOfFireAlarmSensorsId: formData.numberOfFireAlarmSensorsId ?? "",
            numberOfFloorsId: formData.numberOfFloorsId ?? "",
            specialEquipment: formData.specialEquipment ?? "",
            installDate: formData.installDate
                ? moment.utc(formData.installDate).format("YYYY-MM-DD")
                : "",
            cancellationDate: formData.cancellationDate ?? "",
            isActive: formData.isActive ?? true,
            ipaddress: formData.ipaddress ?? "",
            portNumber: formData.portNumber ?? "",
            ipGateway: formData.ipGateway ?? "",
            subnetMask: formData.subnetMask ?? "",
            dns: formData.dns ?? "",
            phoneConnection: formData.phoneConnection ?? "",
            recordNumber: 0
        };

        const updatedList = [...initialAddFireAlarmSystems];
        updatedList[editingIndex] = payload;

        setInitialAddFireAlarmSystems(updatedList);
        setEditingIndex(null);
        setIsEditInAddState(false);
        setShowFireAlarmSystemCardModal(false);
    };

    return (
        <div className="mx-2 mt-2 bg-white box-shadow rounded-lg h-full">
            <div className="flex flex-row justify-between py-2">
                <div className="text-quinary font-bold font-sans px-4">
                    Fire Alarm System Section
                </div>
                {userPermissions.includes(addPermission) ? (
                    <div className="py-2 px-4">
                        <AddButton
                            className={"opacity-100"}
                            disabled={
                                (!fetchingFireAlarmSystems && fireAlarmSystemsData?.length >= MAX_CARD) ||
                                (!!initialAddFireAlarmSystems && ((initialAddFireAlarmSystems as [])?.length >= MAX_CARD))
                            }
                            buttonText="Add Fire Alarm System"
                            onClick={() => {
                                setShowFireAlarmSystemModal(true);
                                setIsAddInEditState(true);
                                resetAlarmForm();
                                setIsRequired(false);
                            }}
                        />
                    </div>
                ) : (
                    <div className="py-5 px-4"></div>
                )}
            </div>

            {/* INITIAL ADD */}
            {isAddState && (
                <div className="text-quinary font-bold font-sans py-2 px-4  grid grid-cols-5 gap-x-4">
                    {Array.isArray(initialAddFireAlarmSystems) && initialAddFireAlarmSystems.length > 0 ? (
                        initialAddFireAlarmSystems.map((fireAlarmSystem, index) => (
                            <FireAlarmSystemCard
                                key={index}
                                fireAlarmSystem={fireAlarmSystem}
                                isEditState={true}
                                hasDeletePermission={hasDeletePermission}
                                onClickView={() => { }}
                                onClickEdit={() => {
                                    setEditFireAlarmSystemValues(fireAlarmSystem);
                                    setFireAlarmForEditData(fireAlarmSystem);
                                    setEditingIndex(index);
                                    setIsEditInAddState(true);
                                    setIsRequired(false);
                                }}
                                onClickDelete={() => {
                                    setShowDeactivateModal(true);
                                }}
                                hideDelete
                                onClickRemove={() => {
                                    handleOpenCardModalRemove(index);
                                }}
                                hideRemove={
                                    (indexToRemove !== null && index === indexToRemove) ||
                                    showFireAlarmSystemCardModal
                                }
                                editHidden={
                                    (indexToRemove !== null && index === indexToRemove) ||
                                    showFireAlarmSystemCardModal
                                }
                            />
                        ))
                    ) : (
                        <div></div>
                    )}
                </div>
            )}

            {/* Show card edit button if edit state*/}
            {isEditState && (
                <div className="w-full borderContainer -mt-4 pb-4 bg-white px-4">
                    <div className="grid gap-2 grid-cols-5">
                        {/* Card */}
                        {(fetchingFireAlarmSystems ? [] : fireAlarmSystemsData || [])?.map((fireAlarmSystem) => (
                            <div key={fireAlarmSystem.fireAlarmSystemId}>
                                <FireAlarmSystemCard
                                    fireAlarmSystem={fireAlarmSystem}
                                    isEditState={isEditState}
                                    onClickView={() => { }}
                                    onClickEdit={() => {
                                        setEditFireAlarmSystemValues(fireAlarmSystem);
                                        setFireAlarmForEditData(fireAlarmSystem);
                                        setIsRequired(false);
                                    }}
                                    onClickDelete={() => {
                                        setShowDeactivateModal(true);
                                        setForDeleteDetails(fireAlarmSystem);
                                    }}
                                    hasDeletePermission={hasDeletePermission}
                                    hideDelete={showFireAlarmSystemCardModal}
                                    editHidden={showFireAlarmSystemCardModal}
                                    hideRemove
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Add */}
            {isShowFireAlarmSystemModal && (
                <SecurityFireAlarmSystemForm
                    isOpen={isShowFireAlarmSystemModal}
                    onClose={() => setShowFireAlarmSystemModal(false)}
                    mode={isAddState ? "initialAdd" : "add"}
                    isSaving={isSaving}
                    onSubmit={() => {
                        if (isAddState) {
                            checkEmptyFields("proceed");
                            handleInitialAdd("proceed");
                            setHasFirstFieldValue(true);
                        } else {
                            checkEmptyFields("save");
                            handleAddSave();
                        }
                    }}
                    isPhoneConnectionRequired={isPhoneConnectionRequired}
                    formMethods={formMethods}
                    vendorsOption={vendorsOption}
                    dataConnOption={dataConnOption}
                    alarmTypeOption={alarmTypeOption}
                    fireAlarmSensorOptions={fireAlarmSensorOptions}
                    floorCountOptions={numberOfFloorOptions}
                />
            )}

            {/* Edit */}
            {showFireAlarmSystemCardModal && (
                <SecurityFireAlarmSystemForm
                    isOpen={showFireAlarmSystemCardModal}
                    onClose={() => setShowFireAlarmSystemCardModal(false)}
                    mode={isEditInAddState ? "initialEdit" : "edit"}
                    isSaving={isSaving}
                    onSubmit={() => {
                        if (isEditInAddState) {
                            handleInitialEdit();
                        } else {
                            handleEditSave();
                        }
                    }}
                    formMethods={formMethods}
                    vendorsOption={vendorsOption}
                    dataConnOption={dataConnOption}
                    alarmTypeOption={alarmTypeOption}
                    hasDeletePermission={hasDeletePermission}
                    isEditState={isEditState}
                    fireAlarmSensorOptions={fireAlarmSensorOptions}
                    floorCountOptions={numberOfFloorOptions}
                />
            )}

            {/* Deactivate Modal */}
            {showDeactivateModal && (
                <ConfirmBoxDelete
                    ModalTitle="Security Fire Alarm System"
                    qConfirmation={deleteMessage}
                    isOpen={showDeactivateModal}
                    onClose={() => setShowDeactivateModal(false)}
                    onClickFunction={() => handleDeactivate()}
                    cancelButtonRef={undefined}
                />
            )}

            {showRemoveModal && (
                <ConfirmBoxDelete
                    ModalTitle="Fire Alarm System"
                    qConfirmation={removeMessage}
                    isOpen={showRemoveModal}
                    onClose={() => {
                        setShowRemoveModal(false);
                        setIndexToRemove(null);
                    }}
                    onClickFunction={handleRemove}
                    cancelButtonRef={undefined}
                />
            )}
        </div>
    );
};

export default FireAlarmSystemForm;
